import React, { useState } from "react";
import { FaTimes, FaEdit, FaExclamationTriangle } from "react-icons/fa";

const RideStatusUpdateModal = ({ show, ride, onClose, onUpdate }) => {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    "Requested",
    "Accepted",
    "Started",
    "Completed",
    "Cancelled",
    "Planned",
    "Arrived",
  ];

  const handleSubmit = async () => {
    const targetStatus = status || ride.status;
    const isStatusChanged = targetStatus !== ride.status;

    if (isStatusChanged && targetStatus === "Cancelled" && !reason) {
      alert("Please provide a reason for cancellation");
      return;
    }

    setLoading(true);
    try {
      const statusData = {
        status: targetStatus,
        reason: reason || undefined,
        adminNotes: adminNotes || undefined,
      };

      await onUpdate(ride._id, statusData);

      // Reset form
      setStatus("");
      setReason("");
      setAdminNotes("");
    } catch (error) {
      console.error("Status update error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStatus("");
    setReason("");
    setAdminNotes("");
    onClose();
  };

  if (!show || !ride) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaEdit className="text-orange-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Update Ride Status
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes />
          </button>
        </div>

        {/* Ride Info */}
        {ride && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">
              <p className="font-medium text-gray-900 mb-1">
                Ride ID: {ride._id}
              </p>
              <p className="text-gray-600">
                Current Status:
                <span className="ml-1 font-medium text-blue-600">
                  {ride.status}
                </span>
              </p>
              <p className="text-gray-600">
                Customer: {ride.userId?.fullName || "N/A"}
              </p>
              <p className="text-gray-600">
                Driver: {ride.driverId?.fullName || "Not assigned"}
              </p>
            </div>
          </div>
        )}

        {/* Status Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Status {/* New Status <span className="text-red-500">*</span> */}
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Status...</option>
            {statusOptions.map((statusOption) => (
              <option
                key={statusOption}
                value={statusOption}
                disabled={statusOption === ride.status}
              >
                {statusOption} {statusOption === ride.status ? "(Current)" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Reason (required for cancellation) */}
        {status === "Cancelled" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Reason <span className="text-red-500">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Reason...</option>
              <option value="Customer request">Customer request</option>
              <option value="Driver unavailable">Driver unavailable</option>
              <option value="Technical issue">Technical issue</option>
              <option value="Payment failed">Payment failed</option>
              <option value="Weather conditions">Weather conditions</option>
              <option value="Vehicle breakdown">Vehicle breakdown</option>
              <option value="Route blocked">Route blocked</option>
              <option value="Other">Other</option>
            </select>
          </div>
        )}

        {/* Reason Input for other statuses */}
        {status && status !== "Cancelled" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter reason for status change..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}

        {/* Admin Notes */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Admin Notes (Optional)
          </label>
          <textarea
            rows="3"
            placeholder="Add any additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </div>

        {/* Warning for status changes */}
        {status && status !== ride.status && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <FaExclamationTriangle className="text-yellow-600 mr-2 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Status Change Warning</p>
                <p>
                  Changing status from "{ride.status}" to "{status}" will:
                </p>
                <ul className="list-disc list-inside mt-1 text-xs">
                  {status === "Cancelled" && (
                    <>
                      <li>
                        Cancel the ride and notify both customer and driver
                      </li>
                      <li>Process any refunds if payment was made</li>
                      <li>Update driver availability</li>
                    </>
                  )}
                  {status === "Completed" && (
                    <>
                      <li>Mark the ride as completed</li>
                      <li>Process payment and commission</li>
                      <li>Send completion notifications</li>
                    </>
                  )}
                  {status === "Started" && (
                    <>
                      <li>Start the ride timer</li>
                      <li>Notify customer that ride has started</li>
                      <li>Enable live tracking</li>
                    </>
                  )}
                  {(status === "Requested" ||
                    status === "Accepted" ||
                    status === "Planned") && (
                    <li>Update ride status and send notifications</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            // disabled={!status || loading || status === ride.status}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              status === "Cancelled"
                ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                : status === "Completed"
                ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
            }`}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Updating...
              </div>
            ) : (
              `Update to ${status || "Status"}`
            )}
          </button>
        </div>

        {/* Status Guidelines */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <strong>Status Guidelines:</strong>
          </p>
          <ul className="text-xs text-blue-700 mt-1 space-y-1">
            <li>
              <strong>Requested:</strong> Customer has requested a ride
            </li>
            <li>
              <strong>Accepted:</strong> Driver has accepted the ride
            </li>
            <li>
              <strong>Started:</strong> Ride is in progress
            </li>
            <li>
              <strong>Completed:</strong> Ride finished successfully
            </li>
            <li>
              <strong>Cancelled:</strong> Ride was cancelled
            </li>
            <li>
              <strong>Planned:</strong> Ride is scheduled for later
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RideStatusUpdateModal;
